import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography, Button, Box, Avatar, Paper, CircularProgress } from "@mui/material";
import AppHeader from "./AppHeader";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WeekendIcon from "@mui/icons-material/Weekend";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, BarChart, Bar, Cell,
} from "recharts";

/* ─── Color Palette (preserved) ──────────────────────────────── */
const C = {
  green: "#116e20",
  emerald: "#10b981",
  red: "#f43f5e",
  amber: "#f59e0b",
  blue: "#3b82f6",
  purple: "#8b5cf6",
  dark: "#0f172a",
  gray: "#64748b",
  border: "#e2e8f0",
  bg: "#f8fafc",
  card: "#ffffff",
};

/* ─── Mock data (stable across renders) ──────────────────────── */

const barColors = [C.green, C.blue, C.amber, C.purple, C.emerald, C.red, C.blue];

const toYangonDateKey = (date = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Yangon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

const formatTrendDay = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return String(date.getDate());
};

const formatTrendTooltipDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/* ─── Reusable stat card ─────────────────────────────────────── */
const StatCard = ({ icon, label, value, delta, color }) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 0",
      minWidth: 140,
      p: 2,
      borderRadius: "8px",
      border: `1px solid ${C.border}`,
      bgcolor: C.card,
      display: "flex",
      alignItems: "center",
      gap: 2,
      transition: "box-shadow .25s, transform .25s",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 12px 24px -6px rgba(0,0,0,.06)",
      },
    }}
  >
    <Avatar
      sx={{
        bgcolor: `${color}14`,
        color,
        width: 42,
        height: 42,
        borderRadius: "8px",
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="h5"
        fontWeight={800}
        sx={{ color: C.dark, lineHeight: 1.1, letterSpacing: "-0.5px" }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ color: C.gray, display: "flex", alignItems: "center", gap: 0.5 }}
      >
        {label}
        {delta && (
          <Box
            component="span"
            sx={{
              ml: 0.5,
              color: delta.startsWith("+") ? C.emerald : delta === "Stable" ? C.gray : C.red,
              fontWeight: 800,
            }}
          >
            {delta}
          </Box>
        )}
      </Typography>
    </Box>
  </Paper>
);

/* ─── Main Component ─────────────────────────────────────────── */
const Dashboard = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const userName = user.full_name || user.username || "Admin";

  const [stats, setStats] = useState({
    total_staff: 0,
    present: 0,
    absent: 0,
    late: 0,
    on_leave: 0,
    pending_leave: 0,
    rest_day: 0
  });
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [lateByDept, setLateByDept] = useState([]);
  const [loading, setLoading] = useState(true);

  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
  const headers = useMemo(() => {
    const token = sessionStorage.getItem("token") || "";
    return { 
      "X-Tenant": tenant,
      "Authorization": `Bearer ${token}`
    };
  }, [tenant]);

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    try {
      const todayKey = toYangonDateKey();
      const [statsRes, headcountRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_ATTENDANCE_API_URL}/admin/dashboard-stats`, {
          params: { date: todayKey },
          withCredentials: true,
          headers
        }),
        axios.get(`${process.env.REACT_APP_ATTENDANCE_API_URL}/admin/daily-headcount`, {
          params: { date: todayKey },
          withCredentials: true,
          headers
        })
      ]);
      if (statsRes.data?.success) {
        setAttendanceTrend(statsRes.data.attendanceTrend || []);
        setLateByDept(statsRes.data.lateByDept || []);
      }
      if (headcountRes.data?.success) {
        setStats(headcountRes.data.summary || {
          total_staff: 0,
          present: 0,
          absent: 0,
          late: 0,
          on_leave: 0,
          pending_leave: 0,
          rest_day: 0
        });
      }
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const today = new Date().toLocaleDateString("en-GB", {
    timeZone: "Asia/Yangon",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const statValue = (value) => loading
    ? <CircularProgress size={20} thickness={5} sx={{ color: C.green }} />
    : Number(value || 0).toLocaleString();

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: C.bg,
        overflow: "hidden",          // ← NO SCROLLING
      }}
    >
      {/* APP HEADER (existing) */}
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      {/* DASHBOARD BODY — fills remaining height */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          p: { xs: 2, md: 3 },
          overflow: "hidden",
        }}
      >
        {/* ── ROW 1: Toolbar ──────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ color: C.dark, letterSpacing: "-0.5px" }}
            >
              Dashboard
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ color: C.gray }}>
              {today} · Welcome back, {userName}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddAltIcon />}
              onClick={() => navigate("/staff/new")}
              sx={{
                bgcolor: C.green,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                boxShadow: "none",
                "&:hover": { bgcolor: "#0e5c1a" },
              }}
            >
              New Staff
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SummarizeOutlinedIcon />}
              onClick={() => navigate("/reports")}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                borderColor: C.border,
                color: C.gray,
                "&:hover": { borderColor: C.green, color: C.green },
              }}
            >
              Reports
            </Button>
          </Box>
        </Box>

        {/* ── ROW 2: Daily Snapshot cards ─────────────────────── */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <StatCard
            icon={<PeopleAltOutlinedIcon />}
            label="Total Staff"
            value={statValue(stats.total_staff)}
            color="#1a237e"
          />
          <StatCard
            icon={<CheckCircleOutlineIcon />}
            label="Present"
            value={statValue(stats.present)}
            color="#2e7d32"
          />
          <StatCard
            icon={<PersonOffOutlinedIcon />}
            label="Workday Absent"
            value={statValue(stats.absent)}
            color="#c62828"
          />
          <StatCard
            icon={<CalendarMonthIcon />}
            label="On Leave"
            value={statValue(stats.on_leave)}
            color="#0288d1"
          />
          <StatCard
            icon={<HourglassEmptyIcon />}
            label="Pending Leave"
            value={statValue(stats.pending_leave)}
            color="#7b1fa2"
          />
          <StatCard
            icon={<WeekendIcon />}
            label="Rest Day"
            value={statValue(stats.rest_day)}
            color="#607d8b"
          />
          <StatCard
            icon={<AccessAlarmIcon />}
            label="Late"
            value={statValue(stats.late)}
            color="#ed6c02"
          />
        </Box>

        {/* ── ROW 3: Charts — fills ALL remaining space ───── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 2.5,
            minHeight: 0,              // critical for flex children
          }}
        >
          {/* LEFT: Attendance Trend (takes ~50%) */}
          <Paper
            elevation={0}
            sx={{
              flex: 6,
              minWidth: 0,
              p: 3,
              borderRadius: "20px",
              border: `1px solid ${C.border}`,
              bgcolor: C.card,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexShrink: 0,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: C.dark }}>
                  Attendance Trend
                </Typography>
                <Typography variant="caption" fontWeight={500} sx={{ color: C.gray }}>
                  30-day check-in volume
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "10px",
                  bgcolor: `${C.green}10`,
                  color: C.green,
                }}
              >
                <Typography variant="caption" fontWeight={800}>
                  ↑ 14.2%
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
              {loading ? (
                <CircularProgress sx={{ color: C.green }} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={attendanceTrend}
                    margin={{ top: 8, right: 8, left: -22, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.green} stopOpacity={0.18} />
                        <stop offset="100%" stopColor={C.green} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f1f5f9"
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatTrendDay}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: C.gray, fontSize: 11, fontWeight: 600 }}
                      dy={8}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: C.gray, fontSize: 11, fontWeight: 600 }}
                    />
                    <Tooltip
                      labelFormatter={formatTrendTooltipDate}
                      contentStyle={{
                        borderRadius: 14,
                        border: "none",
                        boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                        padding: "10px 14px",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="present"
                      stroke={C.green}
                      strokeWidth={3}
                      fill="url(#trendFill)"
                      animationDuration={1800}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>

          {/* MIDDLE: Late Check-ins by Dept (takes ~35%) */}
          <Paper
            elevation={0}
            sx={{
              flex: 4.2,
              minWidth: 0,
              p: 3,
              borderRadius: "20px",
              border: `1px solid ${C.border}`,
              bgcolor: C.card,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexShrink: 0,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: C.dark }}>
                  Late Check-ins
                </Typography>
                <Typography variant="caption" fontWeight={500} sx={{ color: C.gray }}>
                  By department
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => navigate("/reports")}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  color: C.green,
                  "&:hover": { bgcolor: `${C.green}08` },
                }}
              >
                View all →
              </Button>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
              {loading ? (
                <CircularProgress sx={{ color: C.green }} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={lateByDept}
                    margin={{ top: 4, right: 8, left: -22, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="dept"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: C.gray, fontSize: 11, fontWeight: 600 }}
                      dy={8}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: C.gray, fontSize: 11, fontWeight: 600 }}
                    />
                    <Tooltip
                      cursor={{ fill: "#f1f5f9", radius: 6 }}
                      contentStyle={{
                        borderRadius: 14,
                        border: "none",
                        boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                      }}
                    />
                    <Bar dataKey="late" radius={[8, 8, 8, 8]} barSize={36}>
                      {lateByDept.map((_, i) => (
                        <Cell key={i} fill={barColors[i % barColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>

          {/* RIGHT: Quick Analytics Shortcuts (takes ~15%) */}
          <Paper
            elevation={0}
            sx={{
              flex: 1.8,
              minWidth: 200,
              p: 2,
              borderRadius: "20px",
              border: `1px solid ${C.border}`,
              bgcolor: C.card,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ mb: 1.5, flexShrink: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: C.dark }}>
                Quick Analytics
              </Typography>
              <Typography variant="caption" fontWeight={500} sx={{ color: C.gray }}>
                Direct report shortcuts
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                minHeight: 0,
                justifyContent: "center",
              }}
            >
              {/* Staff Demographics Shortcut Card */}
              <Paper
                elevation={0}
                onClick={() => navigate("/reports/demographics")}
                sx={{
                  p: 1.5,
                  borderRadius: "16px",
                  border: `1px solid ${C.border}`,
                  cursor: "pointer",
                  transition: "all .25s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px -4px rgba(0,0,0,.06)",
                    borderColor: C.green,
                    "& .shortcut-arrow": {
                      transform: "translateX(4px)",
                      color: C.green,
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${C.green}12`,
                      color: C.green,
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                    }}
                  >
                    <PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.dark }}>
                      Demographics
                    </Typography>
                    <Typography variant="caption" fontWeight={500} sx={{ color: C.gray, display: "block", lineHeight: 1.2 }}>
                      Gender, age & tenure
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIosIcon className="shortcut-arrow" sx={{ color: C.gray, fontSize: 12, transition: "transform .2s, color .2s" }} />
              </Paper>

              {/* Leave Analytics Shortcut Card */}
              <Paper
                elevation={0}
                onClick={() => navigate("/reports/leave-analytics")}
                sx={{
                  p: 1.5,
                  borderRadius: "16px",
                  border: `1px solid ${C.border}`,
                  cursor: "pointer",
                  transition: "all .25s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px -4px rgba(0,0,0,.06)",
                    borderColor: C.blue,
                    "& .shortcut-arrow": {
                      transform: "translateX(4px)",
                      color: C.blue,
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${C.blue}12`,
                      color: C.blue,
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                    }}
                  >
                    <BarChartIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.dark }}>
                      Leave Analytics
                    </Typography>
                    <Typography variant="caption" fontWeight={500} sx={{ color: C.gray, display: "block", lineHeight: 1.2 }}>
                      Type, dept & status
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIosIcon className="shortcut-arrow" sx={{ color: C.gray, fontSize: 12, transition: "transform .2s, color .2s" }} />
              </Paper>

              {/* Late Analytics Shortcut Card */}
              <Paper
                elevation={0}
                onClick={() => navigate("/reports/late-analytics")}
                sx={{
                  p: 1.5,
                  borderRadius: "16px",
                  border: `1px solid ${C.border}`,
                  cursor: "pointer",
                  transition: "all .25s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px -4px rgba(0,0,0,.06)",
                    borderColor: C.red,
                    "& .shortcut-arrow": {
                      transform: "translateX(4px)",
                      color: C.red,
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${C.red}12`,
                      color: C.red,
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                    }}
                  >
                    <BarChartIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.dark }}>
                      Late Analytics
                    </Typography>
                    <Typography variant="caption" fontWeight={500} sx={{ color: C.gray, display: "block", lineHeight: 1.2 }}>
                      Late % by Department
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIosIcon className="shortcut-arrow" sx={{ color: C.gray, fontSize: 12, transition: "transform .2s, color .2s" }} />
              </Paper>

              {/* Leave Calendar Shortcut Card */}
              <Paper
                elevation={0}
                onClick={() => navigate("/reports/leave-calendar")}
                sx={{
                  p: 1.5,
                  borderRadius: "16px",
                  border: `1px solid ${C.border}`,
                  cursor: "pointer",
                  transition: "all .25s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px -4px rgba(0,0,0,.06)",
                    borderColor: C.purple,
                    "& .shortcut-arrow": {
                      transform: "translateX(4px)",
                      color: C.purple,
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${C.purple}12`,
                      color: C.purple,
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.dark }}>
                      Leave Calendar
                    </Typography>
                    <Typography variant="caption" fontWeight={500} sx={{ color: C.gray, display: "block", lineHeight: 1.2 }}>
                      Monthly leave view
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIosIcon className="shortcut-arrow" sx={{ color: C.gray, fontSize: 12, transition: "transform .2s, color .2s" }} />
              </Paper>

              {/* All Reports Shortcut Card */}
              <Paper
                elevation={0}
                onClick={() => navigate("/reports")}
                sx={{
                  p: 1.5,
                  borderRadius: "16px",
                  border: `1px solid ${C.border}`,
                  cursor: "pointer",
                  transition: "all .25s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px -4px rgba(0,0,0,.06)",
                    borderColor: C.purple,
                    "& .shortcut-arrow": {
                      transform: "translateX(4px)",
                      color: C.purple,
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${C.purple}12`,
                      color: C.purple,
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                    }}
                  >
                    <SummarizeOutlinedIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: C.dark }}>
                      All Reports
                    </Typography>
                    <Typography variant="caption" fontWeight={500} sx={{ color: C.gray, display: "block", lineHeight: 1.2 }}>
                      Access report hub
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIosIcon className="shortcut-arrow" sx={{ color: C.gray, fontSize: 12, transition: "transform .2s, color .2s" }} />
              </Paper>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
